import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Video,
  FileText,
  AlertCircle,
  Wifi,
} from "react-feather";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Container } from "reactstrap";
import Chart from "react-apexcharts";
import { getMeetingsAPI as getAllMeetings } from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import KPICard from "../../../Components/shared/KPICard";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./TenantDashboard.module.scss";

export default function TenantDashboard() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await getAllMeetings();
      setMeetings(m.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── KPIs derived from meetings ─────────────────────────
  const todayStr = new Date().toDateString();

  const todayMeetings = useMemo(
    () =>
      meetings.filter((m) => new Date(m.startAt).toDateString() === todayStr),
    [meetings],
  );

  const scheduledCount = useMemo(
    () => meetings.filter((m) => m.status === "scheduled").length,
    [meetings],
  );

  const uniqueChannels = useMemo(
    () => [...new Set(meetings.map((m) => m.channel))].length,
    [meetings],
  );

  const recentBookings = useMemo(() => meetings.slice(0, 5), [meetings]);

  const upcomingMeetings = useMemo(
    () =>
      meetings
        .filter((m) => new Date(m.startAt) >= new Date())
        .sort((a, b) => new Date(a.startAt) - new Date(b.startAt))
        .slice(0, 5),
    [meetings],
  );

  // ── Channel usage donut ────────────────────────────────
  const channelUsage = useMemo(() => {
    const counts = {};
    meetings.forEach((m) => {
      counts[m.channel] = (counts[m.channel] || 0) + 1;
    });
    return Object.entries(counts).map(([channel, value]) => ({
      channel,
      value,
    }));
  }, [meetings]);

  const donutOpts = useMemo(
    () => ({
      chart: { fontFamily: "Roboto, sans-serif" },
      labels: channelUsage.map((x) => x.channel),
      colors: ["#644998", "#8B6FB3", "#A084D6", "#4B3675"],
      legend: {
        position: "bottom",
        offsetY: 10,
        horizontalAlign: "center",
        itemMargin: { horizontal: 12, vertical: 8 },
        fontSize: "13px",
      },
    }),
    [channelUsage],
  );

  const donutSeries = useMemo(
    () => channelUsage.map((x) => x.value),
    [channelUsage],
  );

  // ── Provider bar chart ─────────────────────────────────
  const providerUsage = useMemo(() => {
    const counts = {};
    meetings.forEach((m) => {
      counts[m.provider] = (counts[m.provider] || 0) + 1;
    });
    return Object.entries(counts).map(([provider, count]) => ({
      provider,
      count,
    }));
  }, [meetings]);

  const barOpts = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        offsetY: 8,
        fontFamily: "Roboto, sans-serif",
      },
      colors: ["#644998"],
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      xaxis: {
        categories: providerUsage.map((x) => x.provider),
        labels: { offsetY: 6, style: { fontSize: "12px", colors: "#6b7280" } },
      },
      yaxis: { labels: { style: { fontSize: "12px", colors: "#6b7280" } } },
      dataLabels: { enabled: false },
      grid: {
        strokeDashArray: 4,
        padding: { top: 12, right: 8, bottom: 16, left: 8 },
      },
    }),
    [providerUsage],
  );

  const barSeries = useMemo(
    () => [{ name: "Meetings", data: providerUsage.map((x) => x.count) }],
    [providerUsage],
  );

  // ── Loading / error states ─────────────────────────────
  if (loading && meetings.length === 0) {
    return (
      <Fragment>
        <PageHeader
          title="Dashboard"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Dashboard" },
          ]}
        />
        <Container fluid className="mh-page-body">
          <LoadingSkeleton rows={6} cols={3} />
        </Container>
      </Fragment>
    );
  }

  if (error && meetings.length === 0) {
    return (
      <Fragment>
        <PageHeader
          title="Dashboard"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Dashboard" },
          ]}
        />
        <Container fluid className="mh-page-body">
          <EmptyState
            icon={AlertTriangle}
            title="Could not load dashboard"
            description={error.message}
            actionLabel="Retry"
            onAction={load}
          />
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageHeader
        title="Tenant dashboard"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Dashboard" },
        ]}
        // primaryAction={{
        //   label: "Book meeting",
        //   onClick: () => navigate(companyUrl("meetings/new")),
        // }}
        secondaryActions={[
          // {
          //   label: "Action center",
          //   onClick: () => navigate(companyUrl("dashboard/actions")),
          // },
          {
            label: "Today's schedule",
            onClick: () => navigate(companyUrl("dashboard/schedule")),
          },
          { label: "Refresh", onClick: load },
        ]}
      />

      <Container fluid className="mh-page-body">
        {/* KPI Cards */}
        <Row className="g-4 mb-4">
          <Col md={6} xl={3}>
            <KPICard
              title="Today's meetings"
              value={todayMeetings.length}
              icon={Video}
              color="#0d6efd"
              loading={loading}
            />
          </Col>
          <Col md={6} xl={3}>
            <KPICard
              title="Total meetings"
              value={meetings.length}
              icon={FileText}
              color="#fd7e14"
              to={companyUrl("meetings")}
              loading={loading}
            />
          </Col>
          <Col md={6} xl={3}>
            <KPICard
              title="Scheduled"
              value={scheduledCount}
              icon={AlertCircle}
              color="#198754"
              to={companyUrl("meetings?status=scheduled")}
              loading={loading}
            />
          </Col>
          <Col md={6} xl={3}>
            <KPICard
              title="Active channels"
              value={uniqueChannels}
              icon={Wifi}
              color="#644998"
              loading={loading}
            />
          </Col>
        </Row>

        {/* Charts */}
        <Row className="g-4 mb-4">
          <Col lg={6}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title as="h2" className="h6 mb-3">
                  Meetings by provider
                </Card.Title>
                {providerUsage.length ? (
                  <Chart
                    options={barOpts}
                    series={barSeries}
                    type="bar"
                    height={320}
                  />
                ) : (
                  <p className="text-muted small mb-0">No chart data.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title as="h2" className="h6 mb-3">
                  Channel usage
                </Card.Title>
                {channelUsage.length ? (
                  <Chart
                    options={donutOpts}
                    series={donutSeries}
                    type="donut"
                    height={320}
                  />
                ) : (
                  <p className="text-muted small mb-0">No chart data.</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Bottom row */}
        <Row className="g-4">
          <Col md={4}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title as="h2" className="h6 mb-0">
                    Upcoming meetings
                  </Card.Title>
                  <Button
                    as={Link}
                    variant="light"
                    size="sm"
                    to={companyUrl("meetings")}
                  >
                    View all
                  </Button>
                </div>
                {upcomingMeetings.length === 0 ? (
                  <p className="text-muted small mb-0">No upcoming meetings.</p>
                ) : (
                  upcomingMeetings.map((m) => (
                    <div key={m._id} className={styles.bookingRow}>
                      <div className="fw-medium">{m.subject}</div>
                      <div className="text-muted small">
                        {new Date(m.startAt).toLocaleDateString([], {
                          month: "short",
                          day: "numeric",
                        })}{" "}
                        ·{" "}
                        {new Date(m.startAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        · {m.channel}
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title as="h2" className="h6 mb-0">
                    Recent bookings
                  </Card.Title>
                  <Button
                    as={Link}
                    variant="light"
                    size="sm"
                    to={companyUrl("meetings")}
                  >
                    View all
                  </Button>
                </div>
                {recentBookings.map((b) => (
                  <div key={b._id} className={styles.bookingRow}>
                    <div className="fw-medium">{b.subject}</div>
                    <div className="text-muted small">
                      {b.organizer?.name} ·{" "}
                      {new Date(b.startAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      · {b.channel}
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <Card.Body>
                <Card.Title as="h2" className="h6 mb-3">
                  Today's schedule
                </Card.Title>
                {todayMeetings.length === 0 ? (
                  <p className="text-muted small mb-0">No meetings today.</p>
                ) : (
                  todayMeetings.map((m) => (
                    <div key={m._id} className={styles.bookingRow}>
                      <div className="fw-medium">{m.subject}</div>
                      <div className="text-muted small">
                        {new Date(m.startAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        –{" "}
                        {new Date(m.endAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        · {m.channel}
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
}
